// ---------------------------------------------------------------------------
// buildRevealPlan — turns {nodes, parentEdges} into the ordered list of reveal
// steps that the scroll drives, plus O(1) lookup maps so the renderer can ask
// "is node/edge X revealed at revealIndex i?" without scanning.
//
// Order (matches the product narrative "it draws the map"):
//   0. root node — visible from the very start (index 0).
//   For each top-level branch of the root, biggest subtree first (each is a hub):
//     a. NODES substep — every node in the branch, BFS from the branch root.
//     b. EDGES substep — the branch's parent edges in BFS-depth order: the
//        branch-root's edge up to the root first, then root→children, then
//        deeper. (Iterating the branch's BFS node order and emitting each
//        node's edge-to-its-parent yields exactly this depth ordering.)
//   A trailing sweep appends any nodes/edges left unreached (dirty data /
//   cycles / extra parents) so the graph is always fully assembled at the end.
// ---------------------------------------------------------------------------

import type { MarketingGraphEdge, MarketingGraphNode } from './types';

export type RevealStep =
	| { kind: 'node'; id: string }
	| { kind: 'edge'; source: string; target: string };

export interface RevealPlan {
	steps: RevealStep[];
	/** steps.length — progress * total maps onto a revealIndex in [0, total). */
	total: number;
	/** node id → step index at which it appears. */
	nodeRevealIndex: Map<string, number>;
	/** edge key (`source->target`) → step index at which it appears. */
	edgeRevealIndex: Map<string, number>;
	/** Top-level branch count — drives the scroll track length. */
	batchCount: number;
	rootId: string | null;
}

export const edgeKey = (source: string, target: string): string => `${source}->${target}`;

const EMPTY_PLAN: RevealPlan = {
	steps: [],
	total: 0,
	nodeRevealIndex: new Map(),
	edgeRevealIndex: new Map(),
	batchCount: 0,
	rootId: null,
};

export const buildRevealPlan = (
	nodes: MarketingGraphNode[],
	parentEdges: MarketingGraphEdge[],
): RevealPlan => {
	if (nodes.length === 0) return EMPTY_PLAN;

	// parent (target) → children (sources); child (source) → its first parent.
	const childrenOf = new Map<string, string[]>();
	const parentOf = new Map<string, string>();
	for (const e of parentEdges) {
		const kids = childrenOf.get(e.target) ?? [];
		kids.push(e.source);
		childrenOf.set(e.target, kids);
		if (!parentOf.has(e.source)) parentOf.set(e.source, e.target);
	}

	const childCount = (id: string) => childrenOf.get(id)?.length ?? 0;
	const subtreeSize = (id: string): number => {
		// Cycle-guarded DFS. Tree subtrees are disjoint, so summing across a
		// node's siblings stays O(V) overall for the common (clean) case.
		const seen = new Set<string>();
		const walk = (cur: string): number => {
			if (seen.has(cur)) return 0;
			seen.add(cur);
			let n = 1;
			for (const c of childrenOf.get(cur) ?? []) n += walk(c);
			return n;
		};
		return walk(id);
	};

	// --- root detection (forest-safe) -------------------------------------
	const roots = nodes.map((n) => n.id).filter((id) => !parentOf.has(id));
	let rootId: string;
	let extraTopLevel: string[] = [];
	if (roots.length === 0) {
		// No parent edges at all, or a pure cycle — pick the busiest node.
		rootId = nodes.reduce((best, n) => (childCount(n.id) > childCount(best) ? n.id : best), nodes[0].id);
	} else if (roots.length === 1) {
		rootId = roots[0];
	} else {
		const sorted = [...roots].sort((a, b) => subtreeSize(b) - subtreeSize(a));
		rootId = sorted[0];
		extraTopLevel = sorted.slice(1);
	}

	const steps: RevealStep[] = [{ kind: 'node', id: rootId }];
	const revealedNodes = new Set<string>([rootId]);
	const revealedEdges = new Set<string>();

	const pushEdge = (source: string, target: string) => {
		const key = edgeKey(source, target);
		if (revealedEdges.has(key)) return;
		revealedEdges.add(key);
		steps.push({ kind: 'edge', source, target });
	};

	// BFS the branch rooted at `start`, revealing unseen nodes; returns the
	// node order (used to emit edges in the same depth order).
	const revealBranchNodes = (start: string): string[] => {
		if (revealedNodes.has(start)) return [];
		const order: string[] = [];
		const queue: string[] = [start];
		revealedNodes.add(start);
		while (queue.length) {
			const cur = queue.shift() as string;
			order.push(cur);
			steps.push({ kind: 'node', id: cur });
			for (const child of childrenOf.get(cur) ?? []) {
				if (revealedNodes.has(child)) continue;
				revealedNodes.add(child);
				queue.push(child);
			}
		}
		return order;
	};

	const branchRoots = [...(childrenOf.get(rootId) ?? []), ...extraTopLevel].sort(
		(a, b) => subtreeSize(b) - subtreeSize(a),
	);
	let batchCount = 0;
	for (const branchRoot of branchRoots) {
		if (revealedNodes.has(branchRoot)) continue;
		const order = revealBranchNodes(branchRoot);
		if (order.length === 0) continue;
		batchCount += 1;
		// Edges in BFS-depth order: each node's edge up to its parent. branchRoot
		// connects to the root first; then root→children; then deeper.
		for (const id of order) {
			const parent = parentOf.get(id);
			if (parent && revealedNodes.has(parent)) pushEdge(id, parent);
		}
	}

	// --- completeness sweep (cycles / multi-parent / disconnected) ---------
	for (const n of nodes) {
		if (!revealedNodes.has(n.id)) {
			revealedNodes.add(n.id);
			steps.push({ kind: 'node', id: n.id });
		}
	}
	for (const e of parentEdges) {
		if (revealedNodes.has(e.source) && revealedNodes.has(e.target)) pushEdge(e.source, e.target);
	}

	const nodeRevealIndex = new Map<string, number>();
	const edgeRevealIndex = new Map<string, number>();
	steps.forEach((step, i) => {
		if (step.kind === 'node') {
			if (!nodeRevealIndex.has(step.id)) nodeRevealIndex.set(step.id, i);
		} else {
			edgeRevealIndex.set(edgeKey(step.source, step.target), i);
		}
	});

	return { steps, total: steps.length, nodeRevealIndex, edgeRevealIndex, batchCount, rootId };
};
